/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Global search modal — a documentation-search-style command palette.
// Presentation-only: all data flows in via `onSearchUsers` (remote,
// `GET /v1/users/search`) and `onSearchLocal` (Dexie scans across
// conversations, messages, attachments). The host owns the adapters
// (apps/web/src/shell/hosts/GlobalSearchModalHost.tsx).
//
// UX shape:
//   • Centered dialog, full-viewport scrim.
//   • Big search input at top with keyboard focus on open.
//   • Four sections: People, Conversations, Messages, Files. Each
//     renders up to 5 results, lazy-skeleton during fetch.
//   • Picking a person opens a profile-preview popover (in-modal)
//     with a "Start chat" CTA — the actual chat-start lives on the
//     host. Picking any other row dismisses the modal and routes.
//   • Escape closes; Enter activates the focused row; ↑/↓ navigates.

import { FileText, MessageSquare, Search as SearchIcon, User, X } from "lucide-react-native";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	Modal,
	Pressable,
	ScrollView,
	StyleSheet,
	Text,
	TextInput,
	View,
} from "react-native";
import { Avatar } from "../components/Avatar";
import { MessagePreview } from "../chat/MessagePreview";

export interface GlobalSearchUser {
	readonly id: string;
	readonly username: string;
	readonly displayName: string;
	readonly role?: string;
	readonly company?: string;
	readonly avatarUri?: string;
	readonly presence?: "online" | "away" | "busy" | "offline";
}

export interface GlobalSearchConversation {
	readonly id: string;
	readonly name: string;
	readonly kind: "direct" | "group";
	readonly lastMessagePreview?: string;
}

export interface GlobalSearchMessage {
	readonly id: string;
	readonly conversationId: string;
	readonly conversationName: string;
	readonly snippet: string;
	readonly sentAt: number;
}

export interface GlobalSearchFile {
	readonly id: string;
	readonly messageId: string;
	readonly conversationId: string;
	readonly filename: string;
	readonly mimeType?: string;
	readonly sizeBytes?: number;
}

export interface GlobalSearchLocalResults {
	readonly conversations: readonly GlobalSearchConversation[];
	readonly messages: readonly GlobalSearchMessage[];
	readonly files: readonly GlobalSearchFile[];
}

export interface GlobalSearchModalProps {
	readonly open: boolean;
	readonly onDismiss: () => void;
	readonly onSearchUsers: (
		query: string,
		signal: AbortSignal,
	) => Promise<readonly GlobalSearchUser[]>;
	readonly onSearchLocal: (
		query: string,
		signal: AbortSignal,
	) => Promise<GlobalSearchLocalResults>;
	readonly onStartChatWithUser: (user: GlobalSearchUser) => Promise<void>;
	readonly onPickConversation: (conversationId: string) => void;
	readonly onPickMessage: (conversationId: string, messageId: string) => void;
	readonly onPickFile: (file: GlobalSearchFile) => void;
}

interface PreviewState {
	readonly user: GlobalSearchUser;
	readonly status: "idle" | "starting" | "error";
	readonly error: string | null;
}

const DEBOUNCE_MS = 200;
const MAX_PER_SECTION = 5;

export function GlobalSearchModal(props: GlobalSearchModalProps): JSX.Element | null {
	const {
		open,
		onDismiss,
		onSearchUsers,
		onSearchLocal,
		onStartChatWithUser,
		onPickConversation,
		onPickMessage,
		onPickFile,
	} = props;

	const [query, setQuery] = useState("");
	const [users, setUsers] = useState<readonly GlobalSearchUser[]>([]);
	const [local, setLocal] = useState<GlobalSearchLocalResults>(EMPTY_LOCAL);
	const [loading, setLoading] = useState(false);
	const [preview, setPreview] = useState<PreviewState | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	// Reset on close.
	useEffect(() => {
		if (!open) {
			setQuery("");
			setUsers([]);
			setLocal(EMPTY_LOCAL);
			setLoading(false);
			setPreview(null);
			abortRef.current?.abort();
			abortRef.current = null;
		}
	}, [open]);

	// Debounced query. We run remote + local searches in parallel so
	// the slower one doesn't block the faster section from rendering.
	useEffect(() => {
		if (!open) return;
		const trimmed = query.trim();
		if (trimmed.length === 0) {
			setUsers([]);
			setLocal(EMPTY_LOCAL);
			setLoading(false);
			return;
		}
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		const handle = setTimeout(() => {
			void (async () => {
				try {
					const [remoteUsers, localResults] = await Promise.all([
						onSearchUsers(trimmed, controller.signal).catch(() => []),
						onSearchLocal(trimmed, controller.signal).catch(() => EMPTY_LOCAL),
					]);
					if (controller.signal.aborted) return;
					setUsers(remoteUsers);
					setLocal(localResults);
				} finally {
					if (!controller.signal.aborted) setLoading(false);
				}
			})();
		}, DEBOUNCE_MS);
		return () => {
			clearTimeout(handle);
			controller.abort();
		};
	}, [open, query, onSearchUsers, onSearchLocal]);

	const handleStartChat = useCallback(
		async (user: GlobalSearchUser): Promise<void> => {
			setPreview({ user, status: "starting", error: null });
			try {
				await onStartChatWithUser(user);
				// Host is expected to call `onDismiss()` after a
				// successful start (route change naturally tears the
				// modal down). Defensive: clear preview state.
				setPreview(null);
			} catch (err) {
				const message =
					(err as { serverMessage?: string } | null)?.serverMessage ??
					(err as Error).message ??
					"Couldn't start that chat.";
				setPreview({ user, status: "error", error: message });
			}
		},
		[onStartChatWithUser],
	);

	if (!open) return null;

	const showEmpty =
		query.trim().length > 0 &&
		!loading &&
		users.length === 0 &&
		local.conversations.length === 0 &&
		local.messages.length === 0 &&
		local.files.length === 0;

	return (
		<>
		<Modal
			visible={open}
			transparent
			// Instant open, matching the rest of the V2 modals.
			animationType="none"
			onRequestClose={onDismiss}
		>
			<Pressable
				accessibilityRole="none"
				onPress={onDismiss}
				style={StyleSheet.absoluteFill}
				className="
					z-50 flex-1 items-center justify-start pt-[10vh] px-md
					bg-black/60
				"
			>
				<Pressable
					onPress={() => undefined}
					className="
						w-full max-w-[640px] max-h-[70vh] flex flex-col
						rounded-xl border
						bg-light-surface-primary border-light-border-secondary
						dark:bg-[#1A1D24] dark:border-white/[0.06]
						shadow-2xl
					"
				>
					{/* Search header — large input + close button. */}
					<View className="flex flex-row items-center gap-md px-lg py-md border-b border-light-border-secondary dark:border-white/[0.06]">
						<SearchIcon size={18} strokeWidth={2} color="#888d96" />
						<TextInput
							value={query}
							onChangeText={setQuery}
							placeholder="Search people, conversations, messages, files…"
							autoCapitalize="none"
							autoCorrect={false}
							autoFocus
							className="
								flex-1
								text-base text-light-text-primary dark:text-white
							"
						/>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Close search"
							onPress={onDismiss}
							className="
								w-7 h-7 rounded-md flex items-center justify-center
								hover:bg-light-surface-tertiary
								dark:hover:bg-white/[0.06]
							"
						>
							<X size={16} strokeWidth={2} color="#888d96" />
						</Pressable>
					</View>

					<ScrollView
						accessibilityLiveRegion="polite"
						className="flex-1 min-h-0 px-sm py-md"
					>
						{query.trim().length === 0 ? (
							<EmptyHint />
						) : showEmpty ? (
							<NoResults />
						) : (
							<>
								<ResultSection
									title="People"
									count={users.length}
									loading={loading && users.length === 0}
								>
									{users.slice(0, MAX_PER_SECTION).map((u) => (
										<PersonRow key={u.id} user={u} onPress={() => handleStartChat(u)} />
									))}
								</ResultSection>

								<ResultSection
									title="Conversations"
									count={local.conversations.length}
									loading={loading && local.conversations.length === 0}
								>
									{local.conversations.slice(0, MAX_PER_SECTION).map((c) => (
										<ConversationRow
											key={c.id}
											conversation={c}
											onPress={() => {
												onPickConversation(c.id);
												onDismiss();
											}}
										/>
									))}
								</ResultSection>

								<ResultSection
									title="Messages"
									count={local.messages.length}
									loading={loading && local.messages.length === 0}
								>
									{local.messages.slice(0, MAX_PER_SECTION).map((m) => (
										<MessageRow
											key={m.id}
											message={m}
											onPress={() => {
												onPickMessage(m.conversationId, m.id);
												onDismiss();
											}}
										/>
									))}
								</ResultSection>

								<ResultSection
									title="Files"
									count={local.files.length}
									loading={loading && local.files.length === 0}
								>
									{local.files.slice(0, MAX_PER_SECTION).map((f) => (
										<FileRow
											key={f.id}
											file={f}
											onPress={() => {
												onPickFile(f);
												onDismiss();
											}}
										/>
									))}
								</ResultSection>
							</>
						)}
					</ScrollView>
				</Pressable>
			</Pressable>
		</Modal>

		{/*
			The profile-preview popover lives in its OWN top-level
			<Modal> portal — see ProfilePreviewPopover below. Rendering
			it as a sibling here (rather than inside the search modal's
			portal) ensures RN-Web mounts it AFTER the search portal in
			document.body, so DOM order paints it on top of the search
			card. Keeping it inside the search modal let the search
			card's stacking context (shadow-2xl + nested Pressables)
			rise above the popover, which is the "modal appears behind"
			symptom.
		*/}
		{preview !== null && (
			<ProfilePreviewPopover
				state={preview}
				onClose={() => setPreview(null)}
				onConfirm={() => handleStartChat(preview.user)}
			/>
		)}
		</>
	);
}

const EMPTY_LOCAL: GlobalSearchLocalResults = {
	conversations: [],
	messages: [],
	files: [],
};

// ── Section primitives ───────────────────────────────────────────────

interface ResultSectionProps {
	readonly title: string;
	readonly count: number;
	readonly loading: boolean;
	readonly children: React.ReactNode;
}

function ResultSection({ title, count, loading, children }: ResultSectionProps): JSX.Element | null {
	if (!loading && count === 0) return null;
	return (
		<View className="flex flex-col gap-xs mb-md">
			<View className="flex flex-row items-baseline px-md pt-sm">
				<Text className="font-sans text-caption font-semibold uppercase tracking-wide text-light-text-tertiary dark:text-white/55">
					{title}
				</Text>
				{!loading && count > MAX_PER_SECTION && (
					<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/45 ml-auto">
						Showing {MAX_PER_SECTION} of {count}
					</Text>
				)}
			</View>
			{loading ? <SectionSkeleton /> : <View className="flex flex-col gap-[2px]">{children}</View>}
		</View>
	);
}

function SectionSkeleton(): JSX.Element {
	return (
		<View className="flex flex-col gap-[2px]">
			{Array.from({ length: 2 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton
					key={i}
					className="flex flex-row items-center gap-md px-md py-sm"
				>
					<View className="w-9 h-9 rounded-full bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex-1 flex flex-col gap-xs">
						<View className="h-3 w-2/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="h-3 w-3/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
					</View>
				</View>
			))}
		</View>
	);
}

// ── Row variants ─────────────────────────────────────────────────────

function PersonRow({
	user,
	onPress,
}: {
	user: GlobalSearchUser;
	onPress: () => void;
}): JSX.Element {
	// Defensive: the wire response may have nulls or empty strings the
	// type declares away. Avatar initials + the display-name <Text>
	// must both be robust to an empty display name (which we've seen
	// from /v1/users/search hits with no profile row), so derive a
	// fallback label up front.
	const safeName = user.displayName || user.username || "Unknown";
	const initial = safeName.charAt(0).toUpperCase() || "?";
	return (
		<Pressable
			accessibilityRole="button"
			onPress={onPress}
			className="
				flex flex-row items-center gap-md px-md py-sm rounded-lg
				hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]
			"
		>
			<Avatar
				initials={initial}
				{...(user.avatarUri !== undefined ? { imageUri: user.avatarUri } : {})}
				presence={
					user.presence === "online"
						? "online"
						: user.presence === "away" || user.presence === "busy"
							? "away"
							: user.presence === "offline"
								? "offline"
								: null
				}
				size="small"
			/>
			<View className="flex-1 min-w-0 flex flex-col gap-[2px]">
				<Text
					numberOfLines={1}
					className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white"
				>
					{safeName}
				</Text>
				<Text
					numberOfLines={1}
					className="font-sans text-caption text-light-text-secondary dark:text-white/55"
				>
					{[user.role, user.company].filter(Boolean).join(" · ") || user.username}
				</Text>
			</View>
		</Pressable>
	);
}

function ConversationRow({
	conversation,
	onPress,
}: {
	conversation: GlobalSearchConversation;
	onPress: () => void;
}): JSX.Element {
	return (
		<Pressable
			accessibilityRole="button"
			onPress={onPress}
			className="
				flex flex-row items-center gap-md px-md py-sm rounded-lg
				hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]
			"
		>
			<View
				className="
					w-9 h-9 rounded-full
					bg-light-surface-secondary dark:bg-white/[0.06]
					flex items-center justify-center
				"
			>
				<MessageSquare size={16} strokeWidth={1.75} color="#888d96" />
			</View>
			<View className="flex-1 min-w-0 flex flex-col gap-[2px]">
				<Text
					numberOfLines={1}
					className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white"
				>
					{conversation.name || (conversation.kind === "group" ? "Group" : "Direct chat")}
				</Text>
				{conversation.lastMessagePreview !== undefined &&
					conversation.lastMessagePreview.length > 0 && (
						// Render through the shared conversation-list preview
						// component so kind glyphs (photo / voice / call …) show as
						// monochrome icons instead of the raw stored emoji.
						<MessagePreview
							preview={conversation.lastMessagePreview}
							className="text-light-text-secondary dark:text-white/55"
						/>
					)}
			</View>
		</Pressable>
	);
}

function MessageRow({
	message,
	onPress,
}: {
	message: GlobalSearchMessage;
	onPress: () => void;
}): JSX.Element {
	return (
		<Pressable
			accessibilityRole="button"
			onPress={onPress}
			className="
				flex flex-row items-center gap-md px-md py-sm rounded-lg
				hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]
			"
		>
			<View
				className="
					w-9 h-9 rounded-full
					bg-light-surface-secondary dark:bg-white/[0.06]
					flex items-center justify-center
				"
			>
				<MessageSquare size={16} strokeWidth={1.75} color="#888d96" />
			</View>
			<View className="flex-1 min-w-0 flex flex-col gap-[2px]">
				<Text
					numberOfLines={1}
					className="font-sans text-caption text-light-text-tertiary dark:text-white/55"
				>
					{message.conversationName}
				</Text>
				<Text
					numberOfLines={2}
					className="font-sans text-footnote text-light-text-primary dark:text-white"
				>
					{message.snippet}
				</Text>
			</View>
		</Pressable>
	);
}

function FileRow({ file, onPress }: { file: GlobalSearchFile; onPress: () => void }): JSX.Element {
	return (
		<Pressable
			accessibilityRole="button"
			onPress={onPress}
			className="
				flex flex-row items-center gap-md px-md py-sm rounded-lg
				hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]
			"
		>
			<View
				className="
					w-9 h-9 rounded-md
					bg-light-surface-secondary dark:bg-white/[0.06]
					flex items-center justify-center
				"
			>
				<FileText size={16} strokeWidth={1.75} color="#888d96" />
			</View>
			<View className="flex-1 min-w-0 flex flex-col gap-[2px]">
				<Text
					numberOfLines={1}
					className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white"
				>
					{file.filename}
				</Text>
				<Text
					numberOfLines={1}
					className="font-sans text-caption text-light-text-secondary dark:text-white/55"
				>
					{[file.mimeType, formatBytes(file.sizeBytes)].filter(Boolean).join(" · ")}
				</Text>
			</View>
		</Pressable>
	);
}

// ── Empty / placeholder states ───────────────────────────────────────

function EmptyHint(): JSX.Element {
	return (
		<View className="flex flex-col items-center px-md py-xl gap-sm">
			<User size={20} strokeWidth={1.5} color="#888d96" />
			<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/55 text-center max-w-[360px]">
				Search the directory by name, find a conversation, jump to a message, or open a file shared
				with you.
			</Text>
		</View>
	);
}

function NoResults(): JSX.Element {
	return (
		<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/55 text-center px-md py-xl">
			Nothing matches that search.
		</Text>
	);
}

// ── Profile-preview popover ──────────────────────────────────────────

function ProfilePreviewPopover({
	state,
	onClose,
	onConfirm,
}: {
	state: PreviewState;
	onClose: () => void;
	onConfirm: () => void;
}): JSX.Element {
	const { user, status, error } = state;
	const safeName = user.displayName || user.username || "Unknown";
	const initial = safeName.charAt(0).toUpperCase() || "?";
	// Render in a dedicated <Modal> so RN-Web allocates a second
	// portal in document.body. That portal mounts AFTER the search
	// modal's portal, guaranteeing the popover paints on top no matter
	// what stacking context the search card creates.
	return (
		<Modal visible transparent animationType="none" onRequestClose={onClose}>
		<View
			style={[StyleSheet.absoluteFill, { pointerEvents: 'box-none' }]}
			className="flex items-center justify-center px-md"
		>
			<Pressable
				accessibilityRole="none"
				onPress={onClose}
				style={StyleSheet.absoluteFill}
				className="bg-black/40"
			/>
			<View
				className="
					w-full max-w-[360px] rounded-xl border
					bg-light-surface-primary border-light-border-secondary
					dark:bg-[#1A1D24] dark:border-white/[0.06]
					shadow-2xl p-lg
				"
			>
				<View className="flex flex-row items-center gap-md mb-md">
					<Avatar
						initials={initial}
						{...(user.avatarUri !== undefined ? { imageUri: user.avatarUri } : {})}
						presence={user.presence === "online" ? "online" : null}
						size="medium"
					/>
					<View className="flex-1 min-w-0">
						<Text
							numberOfLines={1}
							className="font-sans text-headline font-bold text-light-text-primary dark:text-white"
						>
							{safeName}
						</Text>
						<Text
							numberOfLines={1}
							className="font-sans text-caption text-light-text-secondary dark:text-white/55"
						>
							{[user.role, user.company].filter(Boolean).join(" · ") || user.username}
						</Text>
					</View>
				</View>

				{error !== null && (
					<Text className="font-sans text-caption text-light-accent-rose dark:text-dark-accent-rose mb-md">
						{error}
					</Text>
				)}

				<View className="flex flex-row gap-sm">
					<Pressable
						accessibilityRole="button"
						onPress={onClose}
						className="
							flex-1 h-10 rounded-lg items-center justify-center
							bg-light-surface-secondary dark:bg-white/[0.06]
						"
					>
						<Text className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white">
							Cancel
						</Text>
					</Pressable>
					<Pressable
						accessibilityRole="button"
						disabled={status === "starting"}
						onPress={onConfirm}
						className="
							flex-1 h-10 rounded-lg items-center justify-center
							bg-light-accent-brand dark:bg-dark-accent-brand
							disabled:opacity-50
						"
					>
						<Text className="font-sans text-footnote font-semibold text-light-text-onAccent dark:text-dark-text-onAccent">
							{status === "starting" ? "Starting…" : "Start chat"}
						</Text>
					</Pressable>
				</View>
			</View>
		</View>
		</Modal>
	);
}

function formatBytes(bytes?: number): string {
	if (bytes === undefined) return "";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
